// Ambassador: Yatharth.
//
import { Box } from '@mui/material';
import {
    GoogleMap as GoogleMapLib,
    OverlayView,
    OverlayViewF,
    useJsApiLoader,
} from '@react-google-maps/api';
import { useEffect, useMemo, useState } from 'react';

import { useAppConfig } from '../../../fetching/api/appConfig';
import { QhpWithLocationInfo } from '../../../fetching/api/liveLocations';
import { envVars } from '../../../utils/envVars';
import { ReturnComponent } from '../../../utils/reactTypes';
import {
    GoogleMapPin,
    getBadgeContentFromStopIndex,
    iconForQhpWithLocationInfo,
    popoverForQhpWithLocationInfo,
} from './helpers/GoogleMapPin';
import { MapClusterHint } from './helpers/MapClusterHint';
import { DEFAULT_CENTER, DEFAULT_ZOOM, autofitMap } from './helpers/centeringAndZoom';
import { useDrawRoutes } from './helpers/drawRoutes';
import { useFetchAndSnapRoutes } from './helpers/fetchAndSnapRoutes';
import { getColorFromLegendState, getGroupColor } from './helpers/mapColors';
import { parseStops, removeNullishStops } from './helpers/parseStops';
import { NullableStop } from './helpers/stopTypes';
import { useSpiderfy } from './helpers/useSpiderfy';

export type GoogleMapStopsProp = NullableStop[];
export type GoogleMapOtherProps = {
    qhpsWithLocationInfo?: QhpWithLocationInfo[];
    drawRoutes?: boolean;
    updateCenterWhenStopsChange?: boolean;
    showBadgeNumbers?: boolean;
    spiderfyOverlaps?: boolean;
};

const MAP_CONTAINER_STYLE = { height: '100%', width: '100%' };

// Stacking order within the shared overlay-mouse-target pane: spiderfied
// (expanded-cluster) pins sit above idle pins so they're never hidden behind a
// neighbor that isn't being interacted with. Idle pins keep the library default.
const PIN_ZINDEX_SPIDERFIED = 100;

// This component draws
//  1. a map.
//  2. pins for each location you pass in.
//  3. routes between the pins if applicable.
//
// The cool part is we don't erase and redraw the map, which would
//  cause flickering. Instead, we just add and remove the pins and
//  lines we need to. No flickering!
//
// We use the Google Maps Routes API, to draw road-aligned routes
//  between two points that we can draw.
//
// One trickiness is that the stops are sometimes misaligned with the
//  routes. E.g., if a stop is on top of a house, the directions go
//  over the road, so the pin for the house does not sit on top of the
//  route. This looks weird, so to solve that we "snap" the coordinates
//  of the stop to an appropriate point in the route to make things look nice.
//
// ⚠️ You should probably use <GoogleMapWithAtom/> instead of using this directly.
//
export const GoogleMap = ({
    stops: nullableStops,
    qhpsWithLocationInfo = [],
    drawRoutes: drawRoutesRaw = true,
    updateCenterWhenStopsChange = true,
    showBadgeNumbers = true,
    spiderfyOverlaps = false,
}: { stops: GoogleMapStopsProp } & GoogleMapOtherProps): ReturnComponent => {
    //

    const appConfig = useAppConfig();

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: envVars.googleMapsApiKey,
    });

    // We only enable route-drawing functionality based on a permission.
    const drawRoutes = useMemo(
        () => drawRoutesRaw && appConfig.visit.draw_routes_on_visits_page,
        [drawRoutesRaw, appConfig],
    );

    // These are null now, but will eventually be set.
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [maps, setMaps] = useState<typeof google.maps | null>(null);

    // Remove nullish stops.
    const stops = useMemo(() => removeNullishStops(nullableStops), [nullableStops]);

    // We only recalculate the center of the map if the caller asked us to do so.
    useEffect(() => {
        if (updateCenterWhenStopsChange) {
            autofitMap({ map, maps, latLngs: stops });
        }
    }, [map, maps, stops, updateCenterWhenStopsChange]);

    // Calculate pin and route locations.
    const [pinInfoForUnroutedStops, routes] = useMemo(() => parseStops(stops), [stops]);
    const [pinInfoForRoutedStops, routeResults] = useFetchAndSnapRoutes({
        map,
        maps,
        routes,
        drawRoutes,
    });
    const allPinInfos = useMemo(
        () => [...pinInfoForUnroutedStops, ...pinInfoForRoutedStops],
        [pinInfoForUnroutedStops, pinInfoForRoutedStops],
    );

    // Draw the lines between the pins.
    useDrawRoutes({ map, maps, routeResults, drawRoutes });

    // Spiderfy overlapping pins (opt-in).
    const {
        getPinPosition,
        handlePinClick,
        shouldShowPopover,
        getCollapsedClusterSize,
        isPinSpiderfied,
    } = useSpiderfy({
        map,
        maps,
        allPinInfos,
        enabled: spiderfyOverlaps,
    });

    // Memoize the icons for the QHP locations.
    // If we don't do this, then on every render, we generate a new icon component,
    //  which means if a MUI popover component anchored to that icon was open,
    //  it loses the anchor and gets displaced.
    const qhpWithLocationInfoIcons = useMemo(
        () =>
            qhpsWithLocationInfo.map((qhpWithLocationInfo) =>
                iconForQhpWithLocationInfo(qhpWithLocationInfo),
            ),
        [qhpsWithLocationInfo],
    );

    // Guard required: `google.maps` (ControlPosition, etc.) is only available after
    //  the Maps JS API script loads. Rendering below this point is safe.
    if (!isLoaded) {
        return <Box height='100%' width='100%' />;
    }

    return (
        <Box height='100%' width='100%'>
            <GoogleMapLib
                mapContainerStyle={MAP_CONTAINER_STYLE}
                zoom={DEFAULT_ZOOM}
                center={DEFAULT_CENTER}
                onLoad={(googleMap) => {
                    setMap(googleMap);
                    setMaps(google.maps);
                }}
                options={{
                    zoomControl: true,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition.TOP_RIGHT,
                    },
                    mapTypeControl: false,
                }}
            >
                {/* Draw the pins. */}
                {allPinInfos.map((pinInfo, index) => {
                    const pos = getPinPosition(index, pinInfo.stop.lat, pinInfo.stop.lng);
                    const zIndex = isPinSpiderfied(index) ? PIN_ZINDEX_SPIDERFIED : undefined;
                    return (
                        <OverlayViewF
                            key={`pinInfo-${index}`}
                            position={{
                                lat: parseFloat(pos.lat),
                                lng: parseFloat(pos.lng),
                            }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            zIndex={zIndex}
                        >
                            <GoogleMapPin
                                popoverComponent={(() => {
                                    // Collapsed cluster pin: hint to expand instead of a
                                    // dead pin. Otherwise show the stop's own info (when
                                    // not suppressed by spiderfy).
                                    const collapsedClusterSize = getCollapsedClusterSize(index);
                                    if (collapsedClusterSize !== null) {
                                        return <MapClusterHint count={collapsedClusterSize} />;
                                    }
                                    return shouldShowPopover(index)
                                        ? pinInfo.stop.popoverComponent
                                        : undefined;
                                })()}
                                onClick={handlePinClick(index, pinInfo.stop.onClick)}
                                color={
                                    pinInfo.stop.type === 'liveView'
                                        ? getColorFromLegendState(pinInfo.stop.state)
                                        : getGroupColor(pinInfo.groupIndex)
                                }
                                badgeContent={
                                    showBadgeNumbers
                                        ? getBadgeContentFromStopIndex(pinInfo.stopIndex)
                                        : undefined
                                }
                                CustomPinIcon={
                                    pinInfo.stop.type !== 'liveView'
                                        ? pinInfo.stop.customPinIcon
                                        : undefined
                                }
                            />
                        </OverlayViewF>
                    );
                })}
                {/* Draw the live locations of en route QHPs. */}
                {qhpsWithLocationInfo.map((qhpWithLocationInfo, idx) => (
                    <OverlayViewF
                        key={qhpWithLocationInfo.qhp.uuid}
                        position={{
                            lat: parseFloat(qhpWithLocationInfo.location_info.lat),
                            lng: parseFloat(qhpWithLocationInfo.location_info.lng),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                        <GoogleMapPin
                            CustomPinIcon={qhpWithLocationInfoIcons[idx]}
                            popoverComponent={popoverForQhpWithLocationInfo(qhpWithLocationInfo)}
                            color={getGroupColor(null)}
                        />
                    </OverlayViewF>
                ))}
            </GoogleMapLib>
        </Box>
    );
};
